import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  BackOrderRow, Challan, ChallanRequest, ChallanStatus, ChallanSummary, InvoiceFromChallansRequest, OrderPage, OrderRequest,
  SalesOrder, SalesOrderSettings, SalesOrderStatus,
} from '../interfaces/sales/salesOrder.interface';
import { SalesInvoice } from '../interfaces/sales/salesInvoice.interface';
import { LineSerialsRequest } from '../interfaces/inventory/serial.interface';

export const SalesOrderService = {
  list: async (institutionId: string, params: { status?: SalesOrderStatus; customerId?: string; salespersonId?: string; unassigned?: boolean; page?: number; size?: number } = {}): Promise<OrderPage> =>
    (await axios.get(ENDPOINTS.SALES_ORDER.LIST(institutionId), { params: { page: 0, size: 20, ...params } })).data,
  get: async (id: string): Promise<SalesOrder> => (await axios.get(ENDPOINTS.SALES_ORDER.GET(id))).data,
  create: async (institutionId: string, dto: OrderRequest): Promise<SalesOrder> =>
    (await axios.post(ENDPOINTS.SALES_ORDER.CREATE(institutionId), dto)).data,
  update: async (id: string, dto: OrderRequest): Promise<SalesOrder> => (await axios.put(ENDPOINTS.SALES_ORDER.UPDATE(id), dto)).data,
  remove: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.SALES_ORDER.DELETE(id)); },
  confirm: async (id: string): Promise<SalesOrder> => (await axios.post(ENDPOINTS.SALES_ORDER.CONFIRM(id))).data,
  close: async (id: string): Promise<SalesOrder> => (await axios.post(ENDPOINTS.SALES_ORDER.CLOSE(id))).data,
  cancel: async (id: string, reason: string): Promise<SalesOrder> =>
    (await axios.post(ENDPOINTS.SALES_ORDER.CANCEL(id), { reason })).data,
  fromQuotation: async (quotationId: string): Promise<SalesOrder> =>
    (await axios.post(ENDPOINTS.SALES_ORDER.FROM_QUOTATION(quotationId))).data,
  backOrders: async (institutionId: string, params: { customerId?: string; itemId?: string; storeId?: string; overdueOnly?: boolean } = {}): Promise<BackOrderRow[]> =>
    (await axios.get(ENDPOINTS.SALES_ORDER.BACK_ORDERS(institutionId), { params })).data,
  getSettings: async (institutionId: string): Promise<SalesOrderSettings> =>
    (await axios.get(ENDPOINTS.SALES_ORDER.SETTINGS(institutionId))).data,
  updateSettings: async (institutionId: string, dto: SalesOrderSettings): Promise<SalesOrderSettings> =>
    (await axios.put(ENDPOINTS.SALES_ORDER.SETTINGS(institutionId), dto)).data,
  invoiceFromChallans: async (institutionId: string, dto: InvoiceFromChallansRequest): Promise<SalesInvoice> =>
    (await axios.post(ENDPOINTS.SALES_ORDER.INVOICE_FROM_CHALLANS(institutionId), dto)).data,
};

export const DeliveryChallanService = {
  list: async (institutionId: string, params: { status?: ChallanStatus; orderId?: string } = {}): Promise<ChallanSummary[]> =>
    (await axios.get(ENDPOINTS.DELIVERY_CHALLAN.LIST(institutionId), { params })).data,
  get: async (id: string): Promise<Challan> => (await axios.get(ENDPOINTS.DELIVERY_CHALLAN.GET(id))).data,
  create: async (institutionId: string, dto: ChallanRequest): Promise<Challan> =>
    (await axios.post(ENDPOINTS.DELIVERY_CHALLAN.CREATE(institutionId), dto)).data,
  update: async (id: string, dto: ChallanRequest): Promise<Challan> => (await axios.put(ENDPOINTS.DELIVERY_CHALLAN.UPDATE(id), dto)).data,
  /** F6b: `serials` maps challan line id -> serials, for serial-tracked items. */
  post: async (id: string, serials?: LineSerialsRequest): Promise<Challan> =>
    (await axios.post(ENDPOINTS.DELIVERY_CHALLAN.POST(id), serials)).data,
  cancel: async (id: string, reason: string): Promise<Challan> =>
    (await axios.post(ENDPOINTS.DELIVERY_CHALLAN.CANCEL(id), { reason })).data,
  downloadPdf: async (id: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.DELIVERY_CHALLAN.PDF(id), { responseType: 'blob' })).data,
};

/** Reads every page of a paged list (no fixed cap), 200 per page, stopping at the last page. */
export async function fetchAllPages<T>(fetchPage: (page: number, size: number) => Promise<{ content?: T[]; totalPages?: number }>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 0; page < 50; page += 1) {
    const res = await fetchPage(page, 200);
    out.push(...(res.content ?? []));
    if (page + 1 >= (res.totalPages ?? 1)) break;
  }
  return out;
}
